import { ConfirmationAction } from '@app/components/ConfirmationAction';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, ListItemIcon, Menu, MenuItem } from '@mui/material';
import { MouseEvent, useRef, useState } from 'react';
import { defineMessage, useIntl } from 'react-intl';

const cloneMenuItemMessage = defineMessage({
  id: 'fragments.videoEditions.clone',
  defaultMessage: 'Clonar proyecto',
});

const deleteMenuItemMessage = defineMessage({
  id: 'common.actions.delete',
  defaultMessage: 'Eliminar',
});

const deleteConfirmMessage = defineMessage({
  id: 'fragments.videoEditions.deleteConfirm',
  defaultMessage: '¿Eliminar este proyecto? Esta acción no se puede deshacer.',
});

export interface ProjectCardOverflowMenuProps {
  onClone: () => void;
  /** When true, shows delete in the menu and mounts the delete confirmation flow. */
  showDelete: boolean;
  onDelete: () => Promise<void>;
}

/**
 * Kebab menu for a video edition project card: clone and optional delete (with confirmation).
 */
export function ProjectCardOverflowMenu({
  onClone,
  showDelete,
  onDelete,
}: ProjectCardOverflowMenuProps) {
  const { $t } = useIntl();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const deleteTriggerRef = useRef<HTMLButtonElement>(null);
  const open = Boolean(anchorEl);

  const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleClone = () => {
    handleCloseMenu();
    onClone();
  };

  const handleDeleteMenuItemClick = () => {
    handleCloseMenu();
    queueMicrotask(() => {
      deleteTriggerRef.current?.click();
    });
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpenMenu}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleClone}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          {$t(cloneMenuItemMessage)}
        </MenuItem>
        {showDelete ? (
          <MenuItem onClick={handleDeleteMenuItemClick} sx={{ color: 'error.main' }}>
            <ListItemIcon sx={{ color: 'inherit' }}>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            {$t(deleteMenuItemMessage)}
          </MenuItem>
        ) : null}
      </Menu>
      {showDelete ? (
        <ConfirmationAction
          message={$t(deleteConfirmMessage)}
          confirmLabel={$t(deleteMenuItemMessage)}
          confirmColor="error"
          onConfirm={onDelete}
        >
          {({ onClick }) => (
            <IconButton
              ref={deleteTriggerRef}
              onClick={onClick}
              size="small"
              sx={{
                position: 'fixed',
                left: -10000,
                top: 0,
                width: 1,
                height: 1,
                p: 0,
                overflow: 'hidden',
              }}
            />
          )}
        </ConfirmationAction>
      ) : null}
    </>
  );
}
